// @ts-strict-ignore
import React from 'react';
import type {
  ComponentProps,
  ComponentType,
  CSSProperties,
  ReactNode,
  SVGProps,
} from 'react';

import { Block } from '@actual-app/components/block';
import { styles } from '@actual-app/components/styles';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';

import { ItemContent } from './ItemContent';

type ItemProps = {
  title: string;
  Icon?:
    | ComponentType<SVGProps<SVGElement>>
    | ComponentType<SVGProps<SVGSVGElement>>;
  shortcut?: string;
  to?: string;
  children?: ReactNode;
  style?: CSSProperties;
  indent?: number;
  onClick?: ComponentProps<typeof ItemContent>['onClick'];
  forceHover?: boolean;
  forceActive?: boolean;
  isNocturne?: boolean;
};

export function Item({
  children,
  Icon,
  title,
  style,
  to,
  onClick,
  indent = 0,
  forceHover = false,
  forceActive = false,
  shortcut,
  isNocturne = false,
}: ItemProps) {
  const hoverStyle = {
    backgroundColor: theme.sidebarItemBackgroundHover,
  };

  const content = (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        height: isNocturne ? 16 : 20,
      }}
    >
      {Icon && <Icon width={15} height={15} />}
      <Block style={{ marginLeft: Icon ? 8 : 0 }}>{title}</Block>
      <View style={{ flex: 1 }} />
      {shortcut && (
        <Block
          style={{
            color: 'inherit',
            fontSize: 10,
            fontWeight: 500,
            opacity: 0.6,
          }}
        >
          {shortcut}
        </Block>
      )}
    </View>
  );

  return (
    <View
      style={{
        flexShrink: 0,
        ...(isNocturne ? { margin: '0 8.4px 2px' } : {}),
        ...style,
      }}
    >
      <ItemContent
        style={{
          ...styles.mediumText,
          paddingTop: isNocturne ? 7 : 9,
          paddingBottom: isNocturne ? 7 : 9,
          paddingLeft: isNocturne ? 12 + indent : 19 + indent,
          paddingRight: isNocturne ? 12 : 10,
          textDecoration: 'none',
          color: theme.sidebarItemText,
          ...(isNocturne
            ? {
                borderRadius: 4,
                fontSize: 13,
                fontWeight: 500,
              }
            : {}),
          ...(forceHover ? hoverStyle : {}),
          ':hover': hoverStyle,
        }}
        forceActive={forceActive}
        activeStyle={
          isNocturne
            ? {
                backgroundColor: theme.buttonNormalSelectedBackground,
                boxShadow: 'inset 2px 0 0 ' + theme.sidebarItemAccentSelected,
                color: theme.sidebarItemTextSelected,
              }
            : {
                borderLeft: '4px solid ' + theme.sidebarItemTextSelected,
                paddingLeft: 19 + indent - 4,
                color: theme.sidebarItemTextSelected,
              }
        }
        to={to}
        onClick={onClick}
      >
        {content}
      </ItemContent>
      {children ? <View style={{ marginTop: 5 }}>{children}</View> : null}
    </View>
  );
}
